// =====================================================================
// Workspace alert-summary API hook (Sprint 3 Phase 3H, 2026-05-31)
// =====================================================================

import { AlertEventSchema, type ParsedAlertEvent } from '@rhia/protocol';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetch } from '@/lib/api';

const SeverityCountsSchema = z.object({
  info: z.number().int().nonnegative(),
  warning: z.number().int().nonnegative(),
  critical: z.number().int().nonnegative(),
});

export const ProjectAlertCountsSchema = z.object({
  project_id: z.string(),
  unacked: z.number().int().nonnegative(),
  acked: z.number().int().nonnegative(),
  assigned: z.number().int().nonnegative(),
  by_severity: SeverityCountsSchema,
});

export const WorkspaceAlertSummarySchema = z.object({
  totals: z.object({
    unacked: z.number().int().nonnegative(),
    acked: z.number().int().nonnegative(),
    assigned: z.number().int().nonnegative(),
    by_severity: SeverityCountsSchema,
  }),
  per_project: z.array(ProjectAlertCountsSchema),
  recent: z.array(AlertEventSchema),
});
export type ParsedProjectAlertCounts = z.infer<typeof ProjectAlertCountsSchema>;
export type ParsedWorkspaceAlertSummary = z.infer<typeof WorkspaceAlertSummarySchema>;
export type { ParsedAlertEvent };

export function useWorkspaceAlertSummary(wid: string) {
  return useQuery<ParsedWorkspaceAlertSummary, Error>({
    queryKey: ['workspaces', wid, 'alert-summary'],
    queryFn: async () => {
      const raw = await apiFetch<unknown>(`/api/workspaces/${wid}/alert-summary`);
      return WorkspaceAlertSummarySchema.parse(raw);
    },
    enabled: !!wid,
    staleTime: 10_000,
    refetchInterval: 30_000,
  });
}
